import { ChecklistMetricStatus } from '../../handoff-checklist-v2/handoff-checklist-v2.types';
import { VoiceAirMetricDefinition } from '../voice-air-metrics.types';
import {
  getBusinessSettingsQueryOptions,
  getAutoRulesQueryOptions,
} from '../voice-air-handoff.queries';
import {
  AutoMessageType_Slug,
  OutboundPhoneNumberSetting_Selection,
} from '@weave/schema-gen-ts/dist/schemas/messaging/auto-rules/v1/models.pb';
import { DestinationType_Enum } from '@weave/schema-gen-ts/dist/schemas/messaging/shared/v1/enums.pb';
import { getParsedErrorMessage } from 'redux/axios';
import { WEAVE_APP_URL } from 'apis/sso/sso-url-config';

export const aiMissedCallTextMetric: VoiceAirMetricDefinition = {
  id: 'ai_missed_call_text',
  name: 'AI Missed Call Text',
  isRequired: true,
  criteria: [
    {
      description: 'First text message for missed calls is configured.',
      whereToFix: {
        label: 'Bridgeline Business Preferences',
        url: `${WEAVE_APP_URL}/home/dashboard#settings/ai-configuration/ai-receptionist/business-preferences/calls-messaging`,
      },
      evaluate: async (ctx) => {
        try {
          const resp = await ctx.queryClient.fetchQuery(
            getBusinessSettingsQueryOptions(ctx.locationId, ctx.orgId)
          );

          const firstMessage =
            resp?.businessSettings?.value?.dentalPreferences?.callsAndMessaging
              ?.firstMessage;
          if (!firstMessage?.trim()) {
            return {
              status: ChecklistMetricStatus.BLOCKED,
              reason: 'missed_call_first_message_missing',
              message: 'First text message for missed calls is not configured.',
            };
          }
          return {
            status: ChecklistMetricStatus.PASSED,
            reason: 'ai_missed_call_text_configured',
          };
        } catch (err) {
          return {
            status: ChecklistMetricStatus.BLOCKED,
            reason: 'business_settings_fetch_failed',
            message:
              getParsedErrorMessage(err) ||
              'Failed to retrieve first text message configuration.',
          };
        }
      },
    },
    {
      description:
        'Missed Call Auto-Message Rule is enabled and configured for AI Receptionist.',
      whereToFix: {
        label: 'Missed Call Text Settings',
        url: `${WEAVE_APP_URL}/home/dashboard#settings/messaging/auto-messages`,
      },
      evaluate: async (ctx) => {
        try {
          const resp = await ctx.queryClient.fetchQuery(
            getAutoRulesQueryOptions(
              ctx.locationId,
              AutoMessageType_Slug.MISSED_CALL,
              ctx.orgId
            )
          );

          const rules = resp?.rules || [];
          const hasValidRule = rules.some(
            (rule) =>
              rule.enabled === true &&
              rule.destinationType === DestinationType_Enum.TRUELARK &&
              rule.outboundPhoneNumbers &&
              Object.values(rule.outboundPhoneNumbers).some(
                (setting) =>
                  !!setting.selection &&
                  [
                    OutboundPhoneNumberSetting_Selection.AI_RECEPTIONIST,
                    OutboundPhoneNumberSetting_Selection.DEFAULT_PHONE_NUMBER,
                  ].includes(setting.selection)
              )
          );

          if (!hasValidRule) {
            return {
              status: ChecklistMetricStatus.BLOCKED,
              reason: 'missed_call_air_rule_missing',
              message:
                'No enabled missed call auto-message rule is configured for AI Receptionist.',
            };
          }
          return {
            status: ChecklistMetricStatus.PASSED,
            reason: 'ai_missed_call_text_configured',
          };
        } catch (err) {
          return {
            status: ChecklistMetricStatus.BLOCKED,
            reason: 'auto_message_rules_fetch_failed',
            message:
              getParsedErrorMessage(err) ||
              'Failed to retrieve missed call auto-message rules configuration.',
          };
        }
      },
    },
  ],
};
